import { ChevronsUpDown, SearchIcon, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useCurrentUser } from "@/queries/users";

const COMMON_EMAIL_DOMAINS = [
  "@gmail.com",
  "@outlook.com",
  "@yahoo.com",
  "@hotmail.com",
  "@icloud.com",
  "@protonmail.com",
  "@aol.com",
  "@mail.com",
];

interface EmailInputProps {
  name: string;
  label?: string;
  placeholder?: string;
  maxEmails?: number | "Unlimited";
  disabled?: boolean;
  required?: boolean;
  helperText?: string;
}

export const EmailInput = ({
  name,
  label = "Email Addresses",
  placeholder = "Enter email addresses",
  maxEmails = 5,
  disabled = false,
  required = false,
  helperText,
}: EmailInputProps) => {
  const form = useFormContext();
  const { data: user } = useCurrentUser();
  const emails = (form.watch(name) ?? []) as string[];
  const [newEmail, setNewEmail] = useState("");
  const [open, setOpen] = useState(false);

  const emailSuggestions = useMemo(() => {
    if (!newEmail || !newEmail.includes("@") || newEmail.includes(".")) {
      return [];
    }

    const currentUserEmailDomain = user?.email ? `@${user.email.split("@")[1]}` : "";
    const domains = [currentUserEmailDomain, ...COMMON_EMAIL_DOMAINS];
    const uniqueDomains = [...new Set(domains)];

    const [localPart, domainPart] = newEmail.split("@");
    if (!domainPart) {
      return uniqueDomains.map(domain => localPart + domain);
    }

    return uniqueDomains
      .filter(domain => domain.toLowerCase().startsWith("@" + domainPart.toLowerCase()))
      .map(domain => localPart + domain);
  }, [newEmail, user?.email]);

  const isValidEmail = (email: string) => z.string().email().safeParse(email).success;

  const canAddMore = typeof maxEmails === "number" ? emails.length < maxEmails : true;

  const handleAddEmail = (email: string) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !isValidEmail(trimmedEmail)) {
      return;
    }
    if (emails.includes(trimmedEmail)) {
      return;
    }
    if (typeof maxEmails === "number" && emails.length >= maxEmails) {
      return;
    }
    form.setValue(name, [...emails, trimmedEmail], {
      shouldValidate: true,
      shouldDirty: true,
    });
    setNewEmail("");
    setOpen(false);
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    form.setValue(
      name,
      emails.filter(e => e !== emailToRemove),
      {
        shouldValidate: true,
        shouldDirty: true,
      },
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && newEmail.trim() && isValidEmail(newEmail.trim())) {
      e.preventDefault();
      handleAddEmail(newEmail);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    const parsedEmails = pastedText
      .split(/[,;\s\n]+/)
      .map(email => email.trim())
      .filter(email => email && isValidEmail(email));

    if (parsedEmails.length > 0) {
      const currentEmails = emails;
      const newEmails = parsedEmails.filter(email => !currentEmails.includes(email));
      const emailsToAdd =
        typeof maxEmails === "number"
          ? newEmails.slice(0, maxEmails - currentEmails.length)
          : newEmails;

      if (emailsToAdd.length > 0) {
        form.setValue(name, [...currentEmails, ...emailsToAdd], {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    } else if (pastedText.trim()) {
      setNewEmail(pastedText.trim());
    }
  };

  return (
    <FormField
      control={form.control}
      name={name}
      render={() => (
        <FormItem className="flex flex-col">
          <FormLabel className="text-sm font-medium text-gray-700">
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
          </FormLabel>
          <FormControl>
            <div className="space-y-2">
              {emails.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {emails.map(email => (
                    <div
                      key={email}
                      className="inline-flex items-center gap-1 rounded-md bg-blue-100 px-2 py-1 text-sm text-blue-800"
                    >
                      <span>{email}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveEmail(email)}
                        className="flex-shrink-0 rounded-sm p-0.5 hover:bg-blue-200"
                        disabled={disabled}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="h-auto min-h-[40px] w-full justify-between"
                    disabled={disabled || !canAddMore}
                  >
                    {!canAddMore ? (
                      <span className="text-sm text-gray-500">
                        {typeof maxEmails === "number" ? (
                          <>
                            Maximum of {maxEmails} {maxEmails === 1 ? "person" : "people"} can be
                            invited
                          </>
                        ) : (
                          "Maximum limit reached"
                        )}
                      </span>
                    ) : (
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        <SearchIcon className="h-4 flex-shrink-0 text-gray-400" />
                        {emails.length === 0 ? (
                          <span className="text-gray-500">{placeholder}</span>
                        ) : (
                          <span className="text-gray-500">
                            {typeof maxEmails === "number" ? (
                              <>
                                {maxEmails - emails.length} more{" "}
                                {maxEmails - emails.length === 1 ? "person" : "people"} can be
                                invited
                              </>
                            ) : (
                              "Unlimited people can be invited"
                            )}
                          </span>
                        )}
                      </div>
                    )}
                    <ChevronsUpDown className="w-4 text-muted-foreground" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="p-0"
                  style={{ width: "var(--radix-popover-trigger-width)" }}
                >
                  <Command>
                    <CommandInput
                      placeholder="Search or enter email..."
                      value={newEmail}
                      onValueChange={setNewEmail}
                      onKeyDown={handleKeyDown}
                      onPaste={handlePaste}
                      disabled={disabled}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {newEmail && isValidEmail(newEmail.trim()) ? (
                          <CommandItem
                            onSelect={() => handleAddEmail(newEmail)}
                            className="cursor-pointer"
                          >
                            Add "{newEmail.trim()}"
                          </CommandItem>
                        ) : (
                          "No suggestions"
                        )}
                      </CommandEmpty>
                      <CommandGroup>
                        {emailSuggestions.length > 0 &&
                          emailSuggestions.slice(0, 5).map(suggestion => (
                            <CommandItem
                              key={suggestion}
                              value={suggestion}
                              onSelect={() => handleAddEmail(suggestion)}
                              className="cursor-pointer"
                            >
                              {suggestion}
                            </CommandItem>
                          ))}
                        {newEmail &&
                          isValidEmail(newEmail.trim()) &&
                          !emailSuggestions.includes(newEmail.trim()) && (
                            <CommandItem
                              onSelect={() => handleAddEmail(newEmail)}
                              className="cursor-pointer"
                            >
                              Add "{newEmail.trim()}"
                            </CommandItem>
                          )}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </FormControl>
          <FormMessage />
          {helperText && <p className="text-sm text-gray-500">{helperText}</p>}
        </FormItem>
      )}
    />
  );
};
